/** @typedef {'local' | 'dev' | 'sb' | 'prod'} HrcEnvironment */

/** @type {Readonly<Record<HrcEnvironment, string>>} */
export const hrcServiceUrls = Object.freeze({
    local: 'http://localhost:3000/biz',
    dev: 'https://dev-hrc-service.pofsis.com/biz',
    sb: 'https://sb-hrc-service.pofsis.com/biz',
    prod: 'https://hrc-service.pofsis.com/biz',
})

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
const isRecord = (value) => typeof value === 'object' && value !== null

/**
 * @param {unknown} session
 * @returns {session is {ainfo: {aid: string}, pinfo: unknown[], uaccess: unknown[], uaddress: unknown[]}}
 */
export const isCompleteHrcSession = (session) => (
    isRecord(session) &&
    isRecord(session.ainfo) &&
    typeof session.ainfo.aid === 'string' &&
    session.ainfo.aid.length > 0 &&
    Array.isArray(session.pinfo) &&
    Array.isArray(session.uaccess) &&
    Array.isArray(session.uaddress)
)

/** @param {string | undefined} configuredEnvironment @returns {HrcEnvironment} */
export const resolveHrcServiceEnvironment = (configuredEnvironment) => {
    if (
        configuredEnvironment === 'prod' ||
        configuredEnvironment === 'sb' ||
        configuredEnvironment === 'dev'
    ) {
        return configuredEnvironment
    }

    // Local HRC Business opens the deployed dev HRC Service app.
    return 'dev'
}

/** @param {string | undefined} [configuredEnvironment] @returns {string} */
export const getHrcServiceUrl = (configuredEnvironment = process.env.REACT_APP_ENV) => (
    hrcServiceUrls[resolveHrcServiceEnvironment(configuredEnvironment)]
)

/**
 * Opens HRC Service in a new tab with the complete session already loaded by HRC.
 * @param {unknown} session
 * @returns {boolean} Whether navigation was started.
 */
export const openHrcService = (session) => {
    if (!isCompleteHrcSession(session)) {
        console.error('[HRC] Cannot open HRC Service: session is incomplete.')
        return false
    }

    const environment = resolveHrcServiceEnvironment(process.env.REACT_APP_ENV)
    const serializedSession = encodeURIComponent(JSON.stringify(session))
    window.open(
        `${hrcServiceUrls[environment]}?__SESSION=${serializedSession}`,
        '_blank',
        'noopener,noreferrer',
    )
    return true
}
